"use client";

import React from "react";
import { Settings2, Droplets, Coffee } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "The Hand Harvest",
  description: "At the peak of ripeness, our dedicated team hand-picks each cluster under the morning mist. This artisanal approach ensures only the finest fruit enters our cellar, preserving the delicate aromatics of our terroir.",
  icon: Settings2
}, {
  title: "Gentle Crushing",
  description: "Using gravity-flow methods and soft-press technology, we gently release the juice from the skins. This delicate process extracts vibrant color and soft tannins without compromising the wine's natural elegance.",
  icon: Droplets
}, {
  title: "Masterful Aging",
  description: "Our vintages rest in French oak barrels within our temperature-controlled caves. Time and wood work in harmony to develop the complex layers of vanilla, spice, and velvet textures that define VineEssence.",
  icon: Coffee
}];
function StepItem({
  step,
  index,
  "data-index": dataIndex
}: {
  step: (typeof steps)[0];
  index: number;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="131465" ref={ref} data-index={dataIndex} className={`relative flex flex-col md:flex-row gap-8 md:gap-12 group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute -top-12 right-0 md:right-auto md:-left-8 text-[10rem] md:text-[14rem] leading-none font-serif text-secondary/10 z-0 select-none pointer-events-none transition-transform duration-700 group-hover:scale-105 group-hover:text-secondary/20">
        {index + 1}
      </div>

      {}
      <div className="relative z-10 flex flex-col items-center shrink-0 pt-4 md:pt-6">
      </div>

      {}
      <div className="relative z-10 flex-1 pt-2 md:pt-10 pb-12 md:pb-20">
        <h3 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
          {step.title}
        </h3>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans tracking-normal">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function BrewingMethods() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="from-vine-to-bottle" className="dark bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-foreground mb-6">
            The Journey of Essence
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-sans tracking-normal">
            At VineEssence, we believe that exceptional wine is born from a dialogue between nature and craft. 
            Discover the meticulous process that transforms our estate grapes into award-winning vintages.
          </p>
        </div>

        {}
        <div className="relative">
          <div className="flex flex-col">
            {steps.map((step, i) => <StepItem key={i} data-index={i} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}